/**
 * @file        src/pages/airports/airportRules.js
 * @description Client mirror of the server airport rules. MUST stay in step with
 *              admin_backend/src/validators/airport.validator.js — same regexes,
 *              same lengths, same messages. The server is the boundary; this is
 *              here so the user finds out before the round trip.
 * @author      Kinetika Labs
 * @created     2026-09-15
 * @changelog
 *   - 2026-09-15 Claude — Initial.
 *   - 2026-09-15 Claude — `countrycode` checked against the ISO-3166 alpha-2
 *                          register in lib/isoCountries.js, mirroring the
 *                          server's utils/isoCountries.js.
 */
import { z } from 'zod';
import { isIsoCode } from '../../lib/isoCountries';

const LETTERS_ONLY = /^[A-Z]+$/;
const PLACE_TEXT = /^[\p{L}\p{M} .'-]+$/u;
const NAME_TEXT = /^[\p{L}\p{M}0-9 .,'()/&-]+$/u;
const HAS_LETTER = /\p{L}/u;

/** Trim and collapse runs of whitespace, exactly as the server stores it. */
export function collapse(s) {
  return String(s ?? '').replace(/\s+/g, ' ').trim();
}

/** One message per field — the first rule that fails is the one worth showing. */
function firstIssue(check) {
  return (value, ctx) => {
    const message = check(value);
    if (message) ctx.addIssue({ code: z.ZodIssueCode.custom, message });
  };
}

function codeField(label, length, known = null) {
  return z.string().transform((s) => collapse(s).toUpperCase()).superRefine(firstIssue((v) => {
    if (!v) return `${label} is required`;
    if (!LETTERS_ONLY.test(v)) return `${label} must be letters only (A–Z) — no spaces, digits or symbols`;
    if (v.length !== length) return `${label} must be exactly ${length} letters`;
    if (known && !known.test(v)) return known.message(v, label);
    return null;
  }));
}

/** Same register the server checks against — "2 letters" alone accepts ZZ. */
const ISO_COUNTRY = {
  test: isIsoCode,
  message: (v, label) => `${label} “${v}” is not a valid ISO country code (e.g. IN, US, GB)`,
};

function textField(label, { min, max, pattern }) {
  return z.string().transform(collapse).superRefine(firstIssue((v) => {
    if (!v) return `${label} is required`;
    if (v.length < min) return `${label} must be at least ${min} characters`;
    if (v.length > max) return `${label} must be at most ${max} characters`;
    if (!HAS_LETTER.test(v)) return `${label} must contain at least one letter`;
    if (!pattern.test(v)) return `${label} contains characters that are not allowed`;
    return null;
  }));
}

export const airportSchema = z.object({
  code: codeField('Airport code', 3),
  name: textField('Airport name', { min: 3, max: 150, pattern: NAME_TEXT }),
  city: textField('City', { min: 2, max: 100, pattern: PLACE_TEXT }),
  citycode: codeField('City code', 3),
  country: textField('Country', { min: 2, max: 100, pattern: PLACE_TEXT }),
  countrycode: codeField('Country code', 2, ISO_COUNTRY),
});

/** Fields typed in upper case, with the input maxLength that goes with each. */
export const CODE_FIELDS = { code: 3, citycode: 3, countrycode: 2 };

/** Validate one field in isolation, for on-blur feedback. Returns a message or ''. */
export function fieldError(key, value) {
  const single = airportSchema.shape[key];
  if (!single) return '';
  const parsed = single.safeParse(value);
  return parsed.success ? '' : parsed.error.issues[0].message;
}
